import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

export interface SourceWeight {
  name: string;
  score: number;
}

export interface TableState {
  selectedView: 'tables' | 'graphs';
  tabIndex: number;
  sourceWeights: SourceWeight[];
  scoreModalOpen: boolean;
  refreshScoresDropdown: number;
  defaultFilterValue: any[];
  
  rowSelection: Record<string, boolean>;
  columnVisibility: Record<string, boolean>;
  columnOrder: string[];
  sorting: Array<{ id: string; desc: boolean }>;
  pagination: {
    pageIndex: number;
    pageSize: number;
  };
  filters: Record<string, any>;
  
  setSelectedView: (view: 'tables' | 'graphs') => void;
  setTabIndex: (index: number) => void;
  setSourceWeights: (weights: SourceWeight[]) => void;
  setScoreModalOpen: (open: boolean) => void;
  setRefreshScoresDropdown: (count: number) => void;
  setDefaultFilterValue: (value: any[]) => void;
  
  setRowSelection: (selection: Record<string, boolean>) => void;
  setColumnVisibility: (visibility: Record<string, boolean>) => void;
  setColumnOrder: (order: string[]) => void;
  setSorting: (sorting: Array<{ id: string; desc: boolean }>) => void;
  setPagination: (pagination: { pageIndex: number; pageSize: number }) => void;
  setFilters: (filters: Record<string, any>) => void;
  
  toggleView: () => void;
  incrementRefreshScores: () => void;
  resetTableState: () => void;
  resetFilters: () => void;
  
  applySourceWeights: (weights: SourceWeight[], tab: string) => void;
  loadSourceWeights: (tab: string) => void;
  clearSourceWeights: (tab: string) => void;
}

const getDefaultTableState = () => ({
  selectedView: 'tables' as const,
  tabIndex: 0,
  sourceWeights: [],
  scoreModalOpen: false,
  refreshScoresDropdown: 0,
  defaultFilterValue: [],
  
  rowSelection: {},
  columnVisibility: {},
  columnOrder: [],
  sorting: [],
  pagination: {
    pageIndex: 0,
    pageSize: 10,
  },
  filters: {},
});

export const useTableStore = create<TableState>()(
  devtools(
    persist(
      (set) => ({
        ...getDefaultTableState(),

        setSelectedView: (selectedView) => set({ selectedView }),
        setTabIndex: (tabIndex) => set({ tabIndex }),
        setSourceWeights: (sourceWeights) => set({ sourceWeights }),
        setScoreModalOpen: (scoreModalOpen) => set({ scoreModalOpen }),
        setRefreshScoresDropdown: (refreshScoresDropdown) => set({ refreshScoresDropdown }),
        setDefaultFilterValue: (defaultFilterValue) => set({ defaultFilterValue }),
        
        setRowSelection: (rowSelection) => set({ rowSelection }),
        setColumnVisibility: (columnVisibility) => set({ columnVisibility }),
        setColumnOrder: (columnOrder) => set({ columnOrder }),
        setSorting: (sorting) => set({ sorting }),
        setPagination: (pagination) => set({ pagination }),
        setFilters: (filters) => set({ filters }),

        toggleView: () => 
          set((state) => ({ 
            selectedView: state.selectedView === 'tables' ? 'graphs' : 'tables' 
          })),

        incrementRefreshScores: () => 
          set((state) => ({ 
            refreshScoresDropdown: state.refreshScoresDropdown + 1 
          })),

        resetTableState: () => set(getDefaultTableState()),

        resetFilters: () => 
          set({ 
            filters: {}, 
            defaultFilterValue: [],
            sorting: [],
            pagination: { pageIndex: 0, pageSize: 10 }
          }),

        applySourceWeights: (weights: SourceWeight[], tab: string) => {
          set({ sourceWeights: weights });
          if (weights.length > 0) {
            sessionStorage.setItem(`sourceWeights_${tab}`, JSON.stringify(weights));
          } else {
            sessionStorage.removeItem(`sourceWeights_${tab}`);
          }
        },

        loadSourceWeights: (tab: string) => {
          const stored = sessionStorage.getItem(`sourceWeights_${tab}`);
          if (stored) {
            try {
              const weights = JSON.parse(stored);
              set({ sourceWeights: weights });
            } catch (e) {
              set({ sourceWeights: [] });
            }
          } else {
            set({ sourceWeights: [] });
          }
        },

        clearSourceWeights: (tab: string) => {
          set({ sourceWeights: [] });
          sessionStorage.removeItem(`sourceWeights_${tab}`);
        },
      }),
      {
        name: 'table-store',
        partialize: (state) => ({
          columnVisibility: state.columnVisibility,
          columnOrder: state.columnOrder,
          pagination: state.pagination,
        }),
      },
    ),
    {
      name: 'table-store',
    },
  ),
);

export const useTableSelectors = () => {
  const table = useTableStore();

  return {
    isTablesView: table.selectedView === 'tables',
    isGraphsView: table.selectedView === 'graphs',
    
    hasSourceWeights: table.sourceWeights.length > 0,
    hasFilters: Object.keys(table.filters).length > 0,
    hasSelection: Object.keys(table.rowSelection).length > 0,
    
    getSelectedRowCount: () => Object.values(table.rowSelection).filter(Boolean).length,
    
    getCurrentTab: () => table.tabIndex,
    
    getTableConfig: () => ({
      rowSelection: table.rowSelection,
      columnVisibility: table.columnVisibility,
      columnOrder: table.columnOrder,
      sorting: table.sorting,
      pagination: table.pagination,
      filters: table.filters,
    }),
    
    getSourceWeightsByName: (name: string) => 
      table.sourceWeights.find(w => w.name === name)?.score || 0,
    
    getTotalWeightedScore: () => 
      table.sourceWeights.reduce((sum, weight) => sum + weight.score, 0),
  };
};